import React from 'react';
import { Box, Divider, styled, Typography } from '@mui/material';

import { Customer, CustomerAddress } from '../models/Customer';
import { Panel } from 'ui/Panel';
import { isDefined, isEmptyString, isString } from 'lib/typeguards';

const StyledBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    overflow: 'auto',
    maxHeight: '500px',
    paddingBottom: theme.spacing(1)
}));

interface IProps {
    customer: Customer;
}

export const CustomerSavedAddressesCard = ({ customer }: IProps) => {
    const hasAddresses = React.useMemo(
        () => isDefined(customer.addresses) && customer.addresses.length > 0,
        [customer.addresses]
    );
    const showAddressName = React.useCallback(
        (entry: CustomerAddress) => isString(entry.name) && !isEmptyString(entry.name),
        []
    );

    const renderAddress = React.useCallback(
        (entry: CustomerAddress, index: number) => {
            const showName = showAddressName(entry);
            return (
                <React.Fragment key={entry._id}>
                    {index > 0 && <Divider variant="fullWidth" />}
                    <Box>
                        {showName && <Typography variant="body1">{entry.name}</Typography>}
                        <Typography variant={showName ? 'body2' : 'body1'}>
                            {entry.address?.line2 ? `${entry.address?.line2}, ` : ''}
                            {entry.address?.line1}
                        </Typography>
                        <Typography variant="body2">
                            {entry.address?.city}
                            {entry.address?.region ? `, ${entry.address.region}` : ''}
                        </Typography>
                        <Typography variant="caption" display="block">
                            {entry.address?.postalCode}
                        </Typography>
                        <Typography variant="caption" display="block">
                            {entry.address?.country}
                        </Typography>
                    </Box>
                </React.Fragment>
            );
        },
        [showAddressName]
    );
    return (
        <Panel title="Saved Addresses" divider>
            {!hasAddresses && (
                <Box padding={2}>
                    <Typography variant="body1" textAlign="center">
                        This customer doesn&apos;t have any stored addresses
                    </Typography>
                </Box>
            )}
            {hasAddresses && <StyledBox>{customer?.addresses?.map(renderAddress)}</StyledBox>}
        </Panel>
    );
};
